import api from './api';
import type {
  Reservation,
  CreateReservationParams,
  UpdateReservationParams,
  CancelReservationParams,
  TimeSlotData,
  TimeSlot,
  SlotModeData,
} from '@app-types/models/reservation';
import type { Plan } from '@app-types/models/plan';
import type { Business } from '@app-types/models/business';

interface ReservationQueryParams {
  staffId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  [key: string]: string | undefined;
}

interface GoogleCalendar {
  id: string;
  summary: string;
  primary?: boolean;
  [key: string]: unknown;
}

interface DeleteTimeSlotParams {
  staffId: string;
  timeSlotId: string;
}

interface ReservationsResponse {
  success: boolean;
  data: Reservation[];
  message?: string;
}

interface PlansResponse {
  success: boolean;
  data: Plan[];
  message?: string;
}

interface BusinessResponse {
  success: boolean;
  data: Business;
  message?: string;
}

interface GoogleCalendarsResponse {
  success: boolean;
  data: GoogleCalendar[];
  message?: string;
}

interface TimeSlotListResponse {
  success: boolean;
  data: TimeSlot[];
  message?: string;
}

export const getReservations = async (params: ReservationQueryParams): Promise<ReservationsResponse> => {
  try {
    const response = await api.get<ReservationsResponse>(`/business/reservations`, { params });
    console.log('Fetched reservations:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error fetching reservations:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const createReservation = async (reservationData: CreateReservationParams): Promise<Reservation> => {
  try {
    const response = await api.post<Reservation>(`/business/create-reservation`, reservationData);
    console.log('Created reservation:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error creating reservation:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const updateReservation = async (reservationId: string, reservationData: UpdateReservationParams): Promise<Reservation> => {
  try {
    const response = await api.put<Reservation>(`/business/update-reservation/${reservationId}`, reservationData);
    console.log('Updated reservation:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error updating reservation:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const approveReservation = async (reservationId: string): Promise<Reservation> => {
  try {
    const response = await api.put<Reservation>(`/business/approve-reservation/${reservationId}`);
    console.log('Approved reservation:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error approving reservation:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const noShowReservation = async (reservationId: string): Promise<Reservation> => {
  try {
    const response = await api.put<Reservation>(`/business/no-show-reservation/${reservationId}`);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error marking reservation as No Show:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const completeReservation = async (reservationId: string): Promise<Reservation> => {
  try {
    const response = await api.put<Reservation>(`/business/complete-reservation/${reservationId}`);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error marking reservation as Completed:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const cancelReservation = async (reservationId: string, cancelData: CancelReservationParams): Promise<Reservation> => {
  try {
    const response = await api.put<Reservation>(`/business/cancel-reservation/${reservationId}`, cancelData);
    console.log('Cancelled reservation:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error cancelling reservation:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const getStylsitReservations = async (): Promise<ReservationsResponse> => {
  try {
    const response = await api.get<ReservationsResponse>(`/business/stylist-reservations`);
    console.log('Fetched reservations:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error fetching reservations:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const getPlans = async (): Promise<PlansResponse> => {
  try {
    const response = await api.get<PlansResponse>(`/business/plans`);
    console.log('Fetched plans:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error fetching plans:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const getBusiness = async (): Promise<BusinessResponse> => {
  try {
    const response = await api.get<BusinessResponse>(`/business`);
    console.log('Fetched business:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error fetching business:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const createStaffTimeSlots = async (timeSlotData: TimeSlotData): Promise<TimeSlot[]> => {
  try {
    console.log('Creating staff time slots:', timeSlotData);
    const response = await api.put<TimeSlot[]>(`/business/create-staff-time-slots`, timeSlotData);
    console.log('Created staff time slots:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error creating staff time slots:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const deleteStaffTimeSlot = async ({ staffId, timeSlotId }: DeleteTimeSlotParams): Promise<TimeSlotListResponse> => {
  try {
    const response = await api.put<TimeSlotListResponse>(`/business/delete-staff-time-slot`, { staffId, timeSlotId });
    console.log('Deleted staff time slot:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error deleting staff time slot:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const updateStaffTimeSlot = async (timeSlotId: string, timeSlotData: Partial<TimeSlot>): Promise<TimeSlot> => {
  try {
    const response = await api.put<TimeSlot>(`/business/update-staff-time-slot`, { timeSlotId, ...timeSlotData });
    console.log('Updated staff time slot:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error updating staff time slot:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const getStaffTimeSlots = async (staffId: string): Promise<TimeSlot[]> => {
  try {
    const response = await api.get<TimeSlot[]>(`/business/staff-time-slots/${staffId}`);
    console.log('Fetched staff time slots:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error fetching staff time slots:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const changeSlotMode = async (staffId: string, modeData: SlotModeData): Promise<{ mode: string }> => {
  try {
    const response = await api.put<{ mode: string }>(`/business/change-slot-mode/${staffId}`, modeData);
    console.log('Changed slot mode:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error changing slot mode:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const googleCalendarAuthRedirect = (stylistId: string, frontendUrl: string): void => {
  console.log('Stylist ID:', stylistId);
  window.location.href = `${import.meta.env.VITE_API_URL}/business/google-auth-redirect?stylistId=${stylistId}&frontendUrl=${frontendUrl}`;
};

export const listGoogleCalendars = async (stylistId: string): Promise<GoogleCalendarsResponse> => {
  try {
    const response = await api.get<GoogleCalendarsResponse>(`/business/google-calendars?stylistId=${stylistId}`);
    console.log('Fetched Google calendars:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error fetching Google calendars:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const syncGoogleEvents = async (stylistId: string, calendarIds: string[]): Promise<unknown> => {
  try {
    const response = await api.post<unknown>(`/business/google-events`, { stylistId, calendarIds });
    console.log('Synced Google events:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error syncing Google events:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const syncIOSCalendar = async (stylistId: string): Promise<unknown> => {
  try {
    const response = await api.post<unknown>(`/business/sync-ios-calendar`, { stylistId });
    console.log('Synced iOS calendar:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error syncing iOS calendar:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

// Native iOS app reads device-calendar (EventKit) events and pushes them; server writes them as blocked slots.
export const pushIOSCalendarEvents = async (
  stylistId: string,
  events: Array<{ id: string; start: string; end: string; summary: string }>
): Promise<unknown> => {
  try {
    const response = await api.post<unknown>(`/business/ios-calendar-events`, { stylistId, events });
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error pushing iOS calendar events:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const saveCalendarsToSync = async (stylistId: string, calendarIds: string[]): Promise<unknown> => {
  try {
    const response = await api.post<unknown>('/business/save-calendars-to-sync', { stylistId, calendarIds });
    console.log('Saved calendars to sync:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error saving calendars to sync:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const getPendingApprovalReservations = async (staffId: string): Promise<ReservationsResponse> => {
  try {
    const response = await api.get<ReservationsResponse>(`/business/pending-approval-reservations/${staffId}`);
    console.log('Fetched pending approval reservations:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error fetching pending approval reservations:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const notifyRearrange = async (reservationId: string): Promise<unknown> => {
  try {
    const response = await api.post<unknown>(`/business/reservations/${reservationId}/notify-rearrange`);
    console.log('Notified for rearrangement:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error notifying for rearrangement:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

export const deleteReservation = async (reservationId: string): Promise<unknown> => {
  try {
    const response = await api.delete<unknown>(`/business/delete-reservation/${reservationId}`);
    console.log('Deleted reservation:', response.data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error deleting reservation:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};
